// src/tabs/IaPromptsSubTab.js
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Plus,
  Trash2,
  Edit3,
  RefreshCw,
  AlertCircle,
  ToggleLeft,
  ToggleRight,
  Sparkles,
  HelpCircle,
  ArrowRightLeft,
  ShoppingBag,
  Gem,
  CheckCircle2
} from "lucide-react";
import { pb } from "../services/pocketbase";
import { isLikelyAdTypePrompt } from "../services/aiPipelineService";

const CHANNEL_TEMPLATES = {
  "Mercado Livre": `Você é especialista em SEO e vendas de joias no Mercado Livre. Seu papel é transformar a descrição da joia em um anúncio de alta conversão para o Mercado Livre.
Regras:
1. Crie um título matador de até 60 caracteres contendo Tipo de Joia + Metal (Prata 925 / Ouro) + Modelo/Detalhe principal (sem pontuação desnecessária).
2. Estruture a descrição com tópicos objetivos (Especificações Técnicas, O que acompanha, Garantia Vitalícia do Metal e Cuidados).
3. Retorne EXCLUSIVAMENTE um objeto JSON estruturado com os campos:
{
  "nome_produto": "Título para Mercado Livre com até 60 caracteres",
  "descricao_breve": "Resumo de destaque com até 150 caracteres",
  "descricao_completa": "Descrição completa formatada em HTML limpo (<p>, <strong>, <ul>, <li>) pronta para leitura rápida",
  "palavras_chave": ["ate", "10", "termos", "de", "busca", "no", "mercado", "livre"],
  "meta_title": "Título SEO com até 60 caracteres",
  "meta_description": "Resumo SEO com até 160 caracteres"
}`,
  "Site": `Você é o Normalizador de Dados do e-commerce da Sempre Joias. Seu papel é receber uma descrição crua e transformá-la em um JSON estruturado com os seguintes campos exatos:
{
  "nome_produto": "Nome Comercial Chamativo do Produto",
  "descricao_breve": "Breve chamada de venda com até 150 caracteres",
  "descricao_completa": "Descrição completa formatada em HTML limpo utilizando tags <p>, <strong>, <ul>, <li> para excelente legibilidade",
  "palavras_chave": ["array", "com", "ate", "10", "palavras-chave", "de", "busca", "SEO"],
  "meta_title": "Título otimizado para o Google (SEO) com até 60 caracteres",
  "meta_description": "Meta descrição otimizada para o Google (SEO) com até 160 caracteres"
}
Retorne exclusivamente o JSON, sem nenhuma outra introdução ou comentários.`,
  "Shopee": `Você é especialista em vendas de joias na Shopee. Transforme a descrição da peça em um anúncio dinâmico, claro e persuasivo para compradores da Shopee, destacando envio seguro, qualidade do metal e ficha técnica em tópicos.
Retorne EXCLUSIVAMENTE um JSON estruturado com as chaves: "nome_produto", "descricao_breve", "descricao_completa" (em HTML limpo), "palavras_chave" (array), "meta_title" e "meta_description".`,
  "Instagram": `Você é Copywriter de Redes Sociais da Sempre Joias. Crie uma legenda envolvente, emocional e sofisticada para post/Reels no Instagram, com chamada para ação (CTA) e hashtags estratégicas.
Retorne EXCLUSIVAMENTE um JSON estruturado com as chaves: "nome_produto" (Headline do post), "descricao_breve" ( Gancho inicial), "descricao_completa" (Legenda completa formatada em HTML com parágrafos e hashtags), "palavras_chave" (array de hashtags sem #), "meta_title" e "meta_description".`,
  "WhatsApp": `Você é Consultor de Vendas VIP da Sempre Joias no WhatsApp. Crie uma mensagem curta, elegante e persuasiva pronta para envio ao cliente no WhatsApp, destacando os diferenciais da peça e especificações técnicas.
Retorne EXCLUSIVAMENTE um JSON estruturado com as chaves: "nome_produto", "descricao_breve", "descricao_completa" (em HTML limpo), "palavras_chave" (array), "meta_title" e "meta_description".`
};

export default function IaPromptsSubTab({ onPromptsChange }) {
  const [prompts, setPrompts] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);

  // Editor modal state
  const [isOpen, setIsOpen] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);

  const [form, setForm] = useState({
    nome: "",
    tipo: "tipo_anuncio", // "tipo_anuncio" | "categoria" | "modelo"
    contexto: "",
    provedor_id: "",
    modelo_override: "",
    system_prompt: "",
    temperatura: 0.7,
    ativo: true,
    ordem: 1
  });

  const notifyPromptsUpdated = useCallback(() => {
    if (typeof onPromptsChange === "function") {
      onPromptsChange();
    }
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("ia-prompts-updated"));
    }
  }, [onPromptsChange]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [promptRecords, providerRecords] = await Promise.all([
        pb.collection("ia_prompts").getFullList({ sort: "ordem" }),
        pb.collection("ia_configs").getFullList({ filter: "ativo=true", sort: "nome_exibicao" })
      ]);

      setProviders(providerRecords);

      // Seed de Prompts iniciais se estiver vazio
      if (promptRecords.length === 0) {
        const defaultProvId = providerRecords.length > 0 ? providerRecords[0].id : null;

        const seeds = [
          {
            nome: "Agente Redator — Categoria Anel",
            tipo: "categoria",
            contexto: "Anel",
            provedor_id: defaultProvId,
            modelo_override: "",
            system_prompt: "Você é o Redator Premium da Sempre Joias. Seu papel é transformar uma ficha técnica de liga e especificações de um ANEL em uma descrição de venda altamente persuasiva, sofisticada e elegante. Foque no design, acabamento, nobreza do metal e no significado do anel. Retorne apenas o texto fluido em linguagem requintada.",
            temperatura: 0.7,
            ativo: true,
            ordem: 1
          },
          {
            nome: "Agente Redator — Categoria Pingente",
            tipo: "categoria",
            contexto: "Pingente",
            provedor_id: defaultProvId,
            modelo_override: "",
            system_prompt: "Você é o Redator Premium da Sempre Joias. Seu papel é transformar especificações de um PINGENTE em uma descrição de venda altamente persuasiva e sofisticada. Destaque a delicadeza dos detalhes, a nobreza e brilho do metal e a simbologia da peça. Retorne apenas o texto fluido.",
            temperatura: 0.7,
            ativo: true,
            ordem: 1
          },
          {
            nome: "Agente Normalizador — Canal Site",
            tipo: "tipo_anuncio",
            contexto: "Site",
            provedor_id: defaultProvId,
            modelo_override: "",
            system_prompt: CHANNEL_TEMPLATES["Site"],
            temperatura: 0.2,
            ativo: true,
            ordem: 2
          }
        ];

        for (const item of seeds) {
          await pb.collection("ia_prompts").create(item);
        }

        const freshPrompts = await pb.collection("ia_prompts").getFullList({ sort: "ordem" });
        setPrompts(freshPrompts);
        notifyPromptsUpdated();
      } else {
        setPrompts(promptRecords);
      }
    } catch (e) {
      console.error("Erro loading ia_prompts:", e);
    } finally {
      setLoading(false);
    }
  }, [notifyPromptsUpdated]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Abre modal já configurado para Categoria (Etapa 1) ou Tipo de Anúncio (Etapa 2)
  const handleOpenNew = (defaultTipo = "tipo_anuncio") => {
    setEditingPrompt(null);
    setForm({
      nome: "",
      tipo: defaultTipo,
      contexto: "",
      provedor_id: providers.length > 0 ? providers[0].id : "",
      modelo_override: "",
      system_prompt: "",
      temperatura: defaultTipo === "tipo_anuncio" ? 0.3 : 0.7,
      ativo: true,
      ordem: defaultTipo === "tipo_anuncio" ? 2 : 1
    });
    setIsOpen(true);
  };

  const handleOpenEdit = (p) => {
    setEditingPrompt(p);
    setForm({
      nome: p.nome,
      tipo: p.tipo,
      contexto: p.contexto,
      provedor_id: p.provedor_id || (providers.length > 0 ? providers[0].id : ""),
      modelo_override: p.modelo_override || "",
      system_prompt: p.system_prompt,
      temperatura: p.temperatura !== undefined ? p.temperatura : 0.7,
      ativo: p.ativo !== false,
      ordem: p.ordem || (p.tipo === "tipo_anuncio" ? 2 : 1)
    });
    setIsOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.nome.trim()) return alert("Digite o nome identificador do agente/pipe.");
    if (!form.contexto.trim()) return alert("Informe o nome do Canal / Tipo de Anúncio ou Categoria (ex: Mercado Livre, Site, Anel).");
    if (!form.system_prompt.trim()) return alert("O prompt de sistema não pode ser vazio.");

    const provId = form.provedor_id ? form.provedor_id.trim() : null;

    const payload = {
      nome: form.nome.trim(),
      tipo: form.tipo,
      contexto: form.contexto.trim(),
      provedor_id: provId || null,
      modelo_override: form.modelo_override.trim(),
      system_prompt: form.system_prompt.trim(),
      temperatura: Number(form.temperatura),
      ordem: Number(form.ordem),
      ativo: Boolean(form.ativo)
    };

    try {
      if (editingPrompt) {
        await pb.collection("ia_prompts").update(editingPrompt.id, payload);
      } else {
        await pb.collection("ia_prompts").create(payload);
      }
      setIsOpen(false);
      await loadData();
      notifyPromptsUpdated();
    } catch (err) {
      console.error("Erro ao salvar prompt no PocketBase:", err);
      let details = err.message;
      if (err.data?.data) {
        const fieldErrors = Object.entries(err.data.data)
          .map(([k, v]) => `${k}: ${v.message || v.code}`)
          .join("; ");
        if (fieldErrors) details += ` (${fieldErrors})`;
      }
      alert("Erro ao salvar prompt: " + details);
    }
  };

  // Alterna rapidamente o tipo de um prompt entre Categoria (Etapa 1) e Tipo de Anúncio (Etapa 2)
  const handleQuickConvertType = async (p, targetTipo) => {
    try {
      const updated = await pb.collection("ia_prompts").update(p.id, {
        tipo: targetTipo,
        ordem: targetTipo === "tipo_anuncio" ? 2 : 1
      });
      setPrompts(prev => prev.map(item => (item.id === p.id ? updated : item)));
      notifyPromptsUpdated();
    } catch (err) {
      alert("Erro ao converter tipo do agente: " + err.message);
    }
  };

  const handleSeedMissingCategories = async () => {
    const existingCategories = new Set(
      prompts.filter(p => p.tipo === "categoria").map(p => (p.contexto || "").toLowerCase().trim())
    );

    const defaultProvId = providers.length > 0 ? providers[0].id : null;

    const templates = [
      {
        nome: "Agente Redator — Categoria Aliança",
        tipo: "categoria",
        contexto: "Aliança",
        provedor_id: defaultProvId,
        modelo_override: "",
        system_prompt: "Você é o Redator Premium da Sempre Joias especialista em Alianças. Seu papel é transformar dados de aro, conforto anatômico, largura e metal em uma descrição comercial apaixonante, sofisticada e memorável. Destaque o simbolismo eterno da união, a precisão e durabilidade do acabamento de fábrica. Retorne apenas o texto fluido em linguagem requintada.",
        temperatura: 0.7,
        ativo: true,
        ordem: 1
      },
      {
        nome: "Agente Redator — Categoria Berloque",
        tipo: "categoria",
        contexto: "Berloque",
        provedor_id: defaultProvId,
        modelo_override: "",
        system_prompt: "Você é o Redator Premium da Sempre Joias especialista em Berloques e Charms colecionáveis. Seu papel é transformar especificações em uma narrativa afetiva e charmosa, ressaltando os detalhes minuciosos esculpidos no metal e a compatibilidade com pulseiras. Retorne apenas o texto fluido.",
        temperatura: 0.7,
        ativo: true,
        ordem: 1
      },
      {
        nome: "Agente Redator — Categoria Corrente e Pulseira",
        tipo: "categoria",
        contexto: "Corrente/Pulseira",
        provedor_id: defaultProvId,
        modelo_override: "",
        system_prompt: "Você é o Redator Premium da Sempre Joias especialista em Correntes, Pulseiras e Colares. Seu papel é descrever a imponência da malha, a segurança do fecho e o brilho do metal com caimento impecável e elegância atemporal.",
        temperatura: 0.7,
        ativo: true,
        ordem: 1
      },
      {
        nome: "Agente Redator — Categoria Medalha e Escapulário",
        tipo: "categoria",
        contexto: "Medalha",
        provedor_id: defaultProvId,
        modelo_override: "",
        system_prompt: "Você é o Redator Premium da Sempre Joias especialista em Joias Religiosas e Medalhas. Seu papel é unir a nobreza e precisão do acabamento ao valor espiritual, proteção e fé representados na joia.",
        temperatura: 0.7,
        ativo: true,
        ordem: 1
      }
    ];

    const toCreate = templates.filter(t => !existingCategories.has(t.contexto.toLowerCase().trim()));

    if (toCreate.length === 0) {
      return alert("Todas as categorias padrão (Aliança, Berloque, Corrente/Pulseira, Medalha) já estão cadastradas!");
    }

    try {
      setLoading(true);
      for (const item of toCreate) {
        const payload = { ...item };
        if (!payload.provedor_id) payload.provedor_id = null;
        await pb.collection("ia_prompts").create(payload);
      }
      await loadData();
      notifyPromptsUpdated();
    } catch (err) {
      alert("Erro ao adicionar agentes: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSeedMissingChannels = async () => {
    const existingChannels = new Set(
      prompts.filter(p => p.tipo === "tipo_anuncio").map(p => (p.contexto || "").toLowerCase().trim())
    );

    const defaultProvId = providers.length > 0 ? providers[0].id : null;

    const channelList = [
      {
        nome: "Agente Normalizador — Canal Mercado Livre",
        tipo: "tipo_anuncio",
        contexto: "Mercado Livre",
        provedor_id: defaultProvId,
        modelo_override: "",
        system_prompt: CHANNEL_TEMPLATES["Mercado Livre"],
        temperatura: 0.3,
        ativo: true,
        ordem: 2
      },
      {
        nome: "Agente Normalizador — Canal Shopee",
        tipo: "tipo_anuncio",
        contexto: "Shopee",
        provedor_id: defaultProvId,
        modelo_override: "",
        system_prompt: CHANNEL_TEMPLATES["Shopee"],
        temperatura: 0.3,
        ativo: true,
        ordem: 2
      },
      {
        nome: "Agente Normalizador — Canal Instagram",
        tipo: "tipo_anuncio",
        contexto: "Instagram",
        provedor_id: defaultProvId,
        modelo_override: "",
        system_prompt: CHANNEL_TEMPLATES["Instagram"],
        temperatura: 0.5,
        ativo: true,
        ordem: 2
      }
    ];

    const toCreate = channelList.filter(c => !existingChannels.has(c.contexto.toLowerCase().trim()));

    if (toCreate.length === 0) {
      return alert("Os canais padrão (Mercado Livre, Shopee e Instagram) já estão cadastrados!");
    }

    try {
      setLoading(true);
      for (const item of toCreate) {
        const payload = { ...item };
        if (!payload.provedor_id) payload.provedor_id = null;
        await pb.collection("ia_prompts").create(payload);
      }
      await loadData();
      notifyPromptsUpdated();
    } catch (err) {
      alert("Erro ao adicionar canais: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remover este prompt do pipeline?")) return;
    try {
      await pb.collection("ia_prompts").delete(id);
      setPrompts(prev => prev.filter(p => p.id !== id));
      notifyPromptsUpdated();
    } catch (e) {
      alert("Erro ao remover: " + e.message);
    }
  };

  const toggleStatus = async (p) => {
    try {
      const updated = await pb.collection("ia_prompts").update(p.id, { ativo: !p.ativo });
      setPrompts(prev => prev.map(item => (item.id === p.id ? updated : item)));
      notifyPromptsUpdated();
    } catch (e) {
      alert("Erro ao alternar status: " + e.message);
    }
  };

  // Separa os prompts para exibição clara nas 2 etapas do pipeline
  const { categoryPrompts, adTypePrompts, misplacedAdPrompts } = useMemo(() => {
    const cats = [];
    const adTypes = [];
    const misplaced = [];

    prompts.forEach(p => {
      if (p.tipo === "tipo_anuncio") {
        adTypes.push(p);
      } else if (isLikelyAdTypePrompt(p)) {
        // Foi salvo como "categoria" ou "modelo", mas tem nome/contexto de Canal (ex: Mercado Livre)
        misplaced.push(p);
        adTypes.push(p);
      } else {
        cats.push(p);
      }
    });

    return {
      categoryPrompts: cats,
      adTypePrompts: adTypes,
      misplacedAdPrompts: misplaced
    };
  }, [prompts]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8 text-slate-500 bg-slate-50">
        <RefreshCw className="animate-spin mr-2" size={20} /> Carregando prompts do pipeline...
      </div>
    );
  }

  const renderPromptCard = (p, isAdTypeSection = false) => {
    const prov = providers.find(pr => pr.id === p.provedor_id);
    const isMisplaced = p.tipo !== "tipo_anuncio" && isLikelyAdTypePrompt(p);

    return (
      <div
        key={p.id}
        className={`p-4 rounded-xl border transition-all flex flex-col gap-3 ${
          !p.ativo
            ? "border-slate-200 bg-slate-100/60 opacity-60"
            : isMisplaced
            ? "border-amber-300 bg-amber-50/40"
            : isAdTypeSection
            ? "border-purple-200 bg-purple-50/20 hover:border-purple-300"
            : "border-blue-200 bg-blue-50/20 hover:border-blue-300"
        }`}
      >
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2 min-w-0">
            <span className="font-extrabold text-xs text-slate-800">{p.nome}</span>
            <span
              className={`px-2 py-0.5 rounded-md text-[10px] font-bold border ${
                p.tipo === "tipo_anuncio"
                  ? "bg-purple-100 text-purple-800 border-purple-200"
                  : "bg-blue-100 text-blue-800 border-blue-200"
              }`}
            >
              Seletor: {p.contexto}
            </span>
            <span className="px-1.5 py-0.5 bg-white text-slate-500 rounded text-[9px] font-mono border border-slate-200">
              {p.tipo === "tipo_anuncio" ? "2ª Etapa (Tipo de Anúncio)" : "1ª Etapa (Categoria)"}
            </span>
          </div>

          <div className="flex items-center gap-2 shrink-0 self-end sm:self-center">
            <button
              type="button"
              onClick={() =>
                handleQuickConvertType(p, p.tipo === "tipo_anuncio" ? "categoria" : "tipo_anuncio")
              }
              className="px-2 py-1 bg-white hover:bg-slate-100 text-slate-600 rounded border border-slate-200 text-[9px] font-bold flex items-center gap-1"
              title={
                p.tipo === "tipo_anuncio"
                  ? "Mover para 1ª Etapa (Categoria da Joia)"
                  : "Mover para 2ª Etapa (Tipo de Anúncio / Canal)"
              }
            >
              <ArrowRightLeft size={10} />
              {p.tipo === "tipo_anuncio" ? "Mover p/ Categoria" : "Mover p/ Tipo de Anúncio"}
            </button>

            <button
              type="button"
              onClick={() => toggleStatus(p)}
              className="text-slate-400 hover:text-slate-650"
              title={p.ativo ? "Ativo no seletor (clique para desativar)" : "Inativo (clique para ativar)"}
            >
              {p.ativo ? (
                <ToggleRight size={26} className="text-emerald-600" />
              ) : (
                <ToggleLeft size={26} className="text-slate-300" />
              )}
            </button>

            <button
              type="button"
              onClick={() => handleOpenEdit(p)}
              className="p-1.5 bg-white hover:bg-slate-100 rounded border border-slate-200"
              title="Editar Prompt"
            >
              <Edit3 size={12} className="text-slate-600" />
            </button>

            <button
              type="button"
              onClick={() => handleDelete(p.id)}
              className="p-1.5 bg-red-50 hover:bg-red-100 rounded border border-red-200"
              title="Excluir"
            >
              <Trash2 size={12} className="text-red-550" />
            </button>
          </div>
        </div>

        {isMisplaced && (
          <div className="p-2.5 bg-amber-100/80 border border-amber-300 rounded-lg flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-[10px] text-amber-900">
            <span>
              <strong>Atenção:</strong> Este pipe foi salvo com tipo <code>{p.tipo}</code>, mas parece ser um{" "}
              <strong>Tipo de Anúncio / Canal</strong>. Ele já foi incluído automaticamente no seletor de Tipo de Anúncio!
            </span>
            <button
              type="button"
              onClick={() => handleQuickConvertType(p, "tipo_anuncio")}
              className="px-2.5 py-1 bg-amber-700 hover:bg-amber-800 text-white rounded font-bold shrink-0 flex items-center gap-1"
            >
              <CheckCircle2 size={11} /> Fixar como Tipo de Anúncio
            </button>
          </div>
        )}

        <p
          className="text-[11px] text-slate-600 bg-white p-2.5 rounded-lg border border-slate-100 italic line-clamp-2"
          title={p.system_prompt}
        >
          "{p.system_prompt}"
        </p>

        <div className="flex flex-wrap gap-4 text-[9px] font-bold text-slate-400">
          <span>
            Provedor: <strong className="text-slate-600">{prov ? prov.nome_exibicao : "Padrão Ativo"}</strong>
          </span>
          {p.modelo_override && (
            <span>
              Modelo Override: <strong className="text-slate-650 font-mono">{p.modelo_override}</strong>
            </span>
          )}
          <span>
            Temp: <strong className="text-slate-600 font-mono">{p.temperatura}</strong>
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* HEADER EXPLICATIVO DO PIPELINE */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 border-b border-slate-100 pb-4">
          <div>
            <h3 className="font-extrabold text-slate-800 text-sm flex items-center gap-1.5">
              <Sparkles size={16} className="text-purple-600" /> Arquitetura do Pipeline de Anúncios (2 Etapas)
            </h3>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Os agentes abaixo abastecem em tempo real os seletores de <strong>Categoria da Joia</strong> e{" "}
              <strong>Tipo de Anúncio (Canal)</strong> no Gerador.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => handleOpenNew("categoria")}
              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-xl text-xs font-bold shadow-sm flex items-center gap-1.5 transition-all"
            >
              <Plus size={14} /> Novo Redator (Categoria)
            </button>
            <button
              type="button"
              onClick={() => handleOpenNew("tipo_anuncio")}
              className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded-xl text-xs font-bold shadow-sm flex items-center gap-1.5 transition-all"
            >
              <Plus size={14} /> Novo Tipo de Anúncio (Canal)
            </button>
          </div>
        </div>

        <div className="p-3 bg-gradient-to-r from-blue-50/80 to-purple-50/80 border border-blue-200/60 rounded-xl flex items-start gap-2.5">
          <HelpCircle size={16} className="text-purple-600 shrink-0 mt-0.5" />
          <div className="text-[11px] text-slate-700 leading-relaxed">
            <strong className="text-slate-900">Como os seletores do Gerador são preenchidos:</strong>{" "}
            A <strong>1ª Etapa (Redatores por Categoria)</strong> alimenta o seletor <em>"Categoria da Joia"</em> (Anel, Aliança, Pingente...).{" "}
            A <strong>2ª Etapa (Tipos de Anúncio / Canais)</strong> alimenta o seletor <em>"Tipo de Anúncio (Normalizador)"</em> (Site, Mercado Livre, Shopee, Instagram...).
          </div>
        </div>
      </div>

      {/* GRID EM 2 COLUNAS: ETAPA 1 (CATEGORIAS) vs ETAPA 2 (TIPOS DE ANÚNCIO / CANAIS) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        {/* COLUNA 1: REDATORES POR CATEGORIA */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 pb-3">
            <div>
              <span className="text-[10px] font-extrabold uppercase tracking-wider text-blue-600 bg-blue-50 px-2 py-0.5 rounded border border-blue-200">
                1ª Etapa do Pipeline
              </span>
              <h4 className="text-sm font-extrabold text-slate-800 mt-1 flex items-center gap-1.5">
                <Gem size={15} className="text-blue-600" /> Redatores por Categoria ({categoryPrompts.length})
              </h4>
              <p className="text-[10px] text-slate-400">Aparecem no seletor "Categoria da Joia"</p>
            </div>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={handleSeedMissingCategories}
                className="bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 px-2.5 py-1.5 rounded-lg text-[10px] font-bold transition-all"
              >
                + Categorias Padrão
              </button>
              <button
                type="button"
                onClick={() => handleOpenNew("categoria")}
                className="bg-blue-600 hover:bg-blue-700 text-white px-2.5 py-1.5 rounded-lg text-[10px] font-bold flex items-center gap-1"
              >
                <Plus size={12} /> Criar
              </button>
            </div>
          </div>

          <div className="space-y-3">
            {categoryPrompts.length > 0 ? (
              categoryPrompts.map(p => renderPromptCard(p, false))
            ) : (
              <div className="flex flex-col items-center justify-center p-6 text-center text-slate-400 border border-dashed border-slate-200 rounded-xl">
                <AlertCircle size={24} className="mb-1 text-slate-300" />
                <span className="text-xs font-semibold">Nenhum redator de categoria cadastrado.</span>
              </div>
            )}
          </div>
        </div>

        {/* COLUNA 2: TIPOS DE ANÚNCIO / CANAIS (NORMALIZADORES) */}
        <div className="bg-white rounded-2xl border border-purple-200 shadow-sm p-5 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 pb-3">
            <div>
              <span className="text-[10px] font-extrabold uppercase tracking-wider text-purple-700 bg-purple-50 px-2 py-0.5 rounded border border-purple-200">
                2ª Etapa do Pipeline
              </span>
              <h4 className="text-sm font-extrabold text-slate-800 mt-1 flex items-center gap-1.5">
                <ShoppingBag size={15} className="text-purple-600" /> Tipos de Anúncio / Canais ({adTypePrompts.length})
              </h4>
              <p className="text-[10px] text-slate-400">Aparecem no seletor "Tipo de Anúncio (Normalizador)"</p>
            </div>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={handleSeedMissingChannels}
                className="bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200 px-2.5 py-1.5 rounded-lg text-[10px] font-bold transition-all"
                title="Adicionar pipes prontos para Mercado Livre, Shopee e Instagram"
              >
                + Canais Padrão (ML/Shopee)
              </button>
              <button
                type="button"
                onClick={() => handleOpenNew("tipo_anuncio")}
                className="bg-purple-600 hover:bg-purple-700 text-white px-2.5 py-1.5 rounded-lg text-[10px] font-bold flex items-center gap-1"
              >
                <Plus size={12} /> Criar
              </button>
            </div>
          </div>

          <div className="space-y-3">
            {adTypePrompts.length > 0 ? (
              adTypePrompts.map(p => renderPromptCard(p, true))
            ) : (
              <div className="flex flex-col items-center justify-center p-6 text-center text-slate-400 border border-dashed border-purple-200 rounded-xl">
                <AlertCircle size={24} className="mb-1 text-purple-300" />
                <span className="text-xs font-semibold">Nenhum Tipo de Anúncio / Canal cadastrado.</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CREATE/EDIT PROMPT MODAL */}
      {isOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-xl w-full shadow-2xl border border-slate-200 flex flex-col max-h-[88vh] animate-scale-up">
            <div className="p-5 border-b border-slate-100 flex justify-between items-center shrink-0">
              <h3 className="text-sm font-extrabold text-slate-800">
                {editingPrompt ? "Editar Agente / Pipe do Pipeline" : "Novo Agente / Pipe no Pipeline"}
              </h3>
              <button onClick={() => setIsOpen(false)} className="text-slate-400 hover:text-slate-600 font-bold">
                ✕
              </button>
            </div>

            <form onSubmit={handleSave} className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-4 text-xs font-semibold">
              {/* ESCOLHA CLARA DA ETAPA DO PIPELINE */}
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                  Qual seletor este Pipe deve alimentar? *
                </label>
                <div className="grid grid-cols-2 gap-2.5">
                  <button
                    type="button"
                    onClick={() => setForm(prev => ({ ...prev, tipo: "tipo_anuncio", ordem: 2 }))}
                    className={`p-3 rounded-xl border text-left transition-all ${
                      form.tipo === "tipo_anuncio"
                        ? "border-purple-600 bg-purple-50 text-purple-900 ring-2 ring-purple-500/20"
                        : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <div className="font-extrabold text-xs flex items-center gap-1.5">
                      <ShoppingBag size={14} className={form.tipo === "tipo_anuncio" ? "text-purple-600" : "text-slate-400"} />
                      Tipo de Anúncio / Canal
                    </div>
                    <p className="text-[10px] font-normal text-slate-500 mt-0.5">
                      Ex: Mercado Livre, Site, Shopee, Instagram (2ª Etapa)
                    </p>
                  </button>

                  <button
                    type="button"
                    onClick={() => setForm(prev => ({ ...prev, tipo: "categoria", ordem: 1 }))}
                    className={`p-3 rounded-xl border text-left transition-all ${
                      form.tipo === "categoria"
                        ? "border-blue-600 bg-blue-50 text-blue-900 ring-2 ring-blue-500/20"
                        : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <div className="font-extrabold text-xs flex items-center gap-1.5">
                      <Gem size={14} className={form.tipo === "categoria" ? "text-blue-600" : "text-slate-400"} />
                      Categoria da Joia
                    </div>
                    <p className="text-[10px] font-normal text-slate-500 mt-0.5">
                      Ex: Anel, Aliança, Pingente, Berloque (1ª Etapa)
                    </p>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <div className="flex justify-between items-center mb-1">
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                      {form.tipo === "tipo_anuncio"
                        ? "Nome no Seletor de Tipo de Anúncio (Canal) *"
                        : "Nome no Seletor de Categoria da Joia *"}
                    </label>
                    <span className="text-[9px] text-slate-400">Clique abaixo para preencher rápido:</span>
                  </div>
                  <input
                    type="text"
                    required
                    value={form.contexto}
                    onChange={(e) => {
                      const val = e.target.value;
                      setForm(prev => ({
                        ...prev,
                        contexto: val,
                        nome:
                          !prev.nome ||
                          prev.nome.startsWith("Agente Normalizador — Canal ") ||
                          prev.nome.startsWith("Agente Redator — Categoria ")
                            ? form.tipo === "tipo_anuncio"
                              ? `Agente Normalizador — Canal ${val}`
                              : `Agente Redator — Categoria ${val}`
                            : prev.nome
                      }));
                    }}
                    placeholder={
                      form.tipo === "tipo_anuncio"
                        ? "Ex: Mercado Livre, Site, Shopee, Instagram"
                        : "Ex: Anel, Aliança, Berloque, Pingente"
                    }
                    className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-purple-500 font-bold"
                  />

                  {/* Sugestões Rápidas de Canais */}
                  {form.tipo === "tipo_anuncio" && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {["Mercado Livre", "Site", "Shopee", "Instagram", "WhatsApp"].map(canal => (
                        <button
                          key={canal}
                          type="button"
                          onClick={() => {
                            setForm(prev => ({
                              ...prev,
                              contexto: canal,
                              nome: prev.nome ? prev.nome : `Agente Normalizador — Canal ${canal}`,
                              system_prompt:
                                !prev.system_prompt.trim() && CHANNEL_TEMPLATES[canal]
                                  ? CHANNEL_TEMPLATES[canal]
                                  : prev.system_prompt
                            }));
                          }}
                          className={`px-2.5 py-1 rounded-md text-[10px] font-bold border transition-all ${
                            form.contexto.toLowerCase() === canal.toLowerCase()
                              ? "bg-purple-600 text-white border-purple-600 shadow-sm"
                              : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-purple-50 hover:border-purple-200"
                          }`}
                        >
                          {canal}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Sugestões Rápidas de Categorias */}
                  {form.tipo === "categoria" && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {[
                        "Anel",
                        "Pingente",
                        "Aliança",
                        "Berloque",
                        "Corrente/Pulseira",
                        "Medalha",
                        "Escapulário",
                        "Formatura",
                        "Joia Exclusiva"
                      ].map(cat => (
                        <button
                          key={cat}
                          type="button"
                          onClick={() => {
                            setForm(prev => ({
                              ...prev,
                              contexto: cat,
                              nome: prev.nome ? prev.nome : `Agente Redator — Categoria ${cat}`
                            }));
                          }}
                          className={`px-2.5 py-1 rounded-md text-[10px] font-bold border transition-all ${
                            form.contexto.toLowerCase() === cat.toLowerCase()
                              ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                              : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-blue-50 hover:border-blue-200"
                          }`}
                        >
                          {cat}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="col-span-2">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                    Nome Interno do Agente / Pipe *
                  </label>
                  <input
                    type="text"
                    required
                    value={form.nome}
                    onChange={(e) => setForm({ ...form, nome: e.target.value })}
                    placeholder="Ex: Pipe de Teste — Anúncio Mercado Livre"
                    className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 font-bold"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                    Provedor IA Ativo
                  </label>
                  <select
                    value={form.provedor_id}
                    onChange={(e) => setForm({ ...form, provedor_id: e.target.value })}
                    className="w-full p-2 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 font-bold bg-white"
                  >
                    <option value="">Provedor Padrão do Sistema</option>
                    {providers.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.nome_exibicao}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                    Modelo Override
                  </label>
                  <input
                    type="text"
                    value={form.modelo_override}
                    onChange={(e) => setForm({ ...form, modelo_override: e.target.value })}
                    placeholder="Ex: gemini-1.5-flash (vazio p/ padrão)"
                    className="w-full p-2 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 font-mono"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                    Temperatura (0.0 a 1.0)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="1"
                    step="0.1"
                    value={form.temperatura}
                    onChange={(e) => setForm({ ...form, temperatura: e.target.value })}
                    className="w-full p-2 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 font-mono"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                    Ordem de Execução
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={form.ordem}
                    onChange={(e) => setForm({ ...form, ordem: e.target.value })}
                    className="w-full p-2 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 font-mono"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                    Prompt de Sistema (System Prompt) *
                  </label>
                  {form.tipo === "tipo_anuncio" && (
                    <button
                      type="button"
                      onClick={() => {
                        const tpl =
                          CHANNEL_TEMPLATES[form.contexto] || CHANNEL_TEMPLATES["Mercado Livre"];
                        setForm(prev => ({ ...prev, system_prompt: tpl }));
                      }}
                      className="text-[10px] font-bold text-purple-600 hover:text-purple-800 underline"
                    >
                      Inserir Template de Anúncio Estruturado
                    </button>
                  )}
                </div>
                <textarea
                  required
                  value={form.system_prompt}
                  onChange={(e) => setForm({ ...form, system_prompt: e.target.value })}
                  rows={6}
                  placeholder="Instruções de personalidade, restrições e regras para a IA..."
                  className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs font-semibold leading-relaxed"
                />
              </div>

              <div className="pt-4 border-t border-slate-100 flex justify-end gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-500 hover:bg-slate-100 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-lg text-xs shadow-sm"
                >
                  Salvar Agente / Pipe
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
